'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { usePathname } from 'next/navigation';
import { useExplorer } from '@/hooks/useExplorer';
import { useCodeGenerationStore } from '@/stores/useCodeGenerationStore';
import { hasAnyUnsavedData } from '@/hooks/useUnsavedChangesWarning';
import { explorerAnalytics } from '@/lib/analytics';
import type { UnsavedChangesVariant } from '@/components/ui/UnsavedChangesDialog';

export interface UnsavedChangesContextProps {
  hasUnsavedData: boolean;
  confirmNavigation: (url: string, variant?: UnsavedChangesVariant) => void;
  confirmAction: (action: () => void) => void;
  dialogState: {
    open: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    onOpenChange: (open: boolean) => void;
    variant: UnsavedChangesVariant;
  };
}

export const UnsavedChangesContext =
  createContext<UnsavedChangesContextProps | null>(null);

export const useUnsavedChanges = (): UnsavedChangesContextProps => {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error(
      'useUnsavedChanges must be used within UnsavedChangesProvider'
    );
  }
  return context;
};

export const UnsavedChangesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { endpointAttributes, userPrompt } = useExplorer();
  const { messages } = useCodeGenerationStore();

  const [showDialog, setShowDialog] = useState(false);
  const [pendingCallback, setPendingCallback] = useState<(() => void) | null>(
    null
  );
  const [dialogVariant, setDialogVariant] =
    useState<UnsavedChangesVariant>('default');

  // Flag to suppress beforeunload when intentionally navigating after user confirmation
  const isIntentionalNavigation = useRef(false);
  const pathname = usePathname();

  // Reset the flag after each client-side route change so that beforeunload
  // is re-armed on the new page (client-side navigation never fires beforeunload,
  // so the flag is no longer needed once the route has settled).
  useEffect(() => {
    isIntentionalNavigation.current = false;
  }, [pathname]);

  const hasUnsavedData = hasAnyUnsavedData(
    endpointAttributes,
    userPrompt,
    messages.length
  );

  const confirmNavigation = useCallback(
    (url: string, variant: UnsavedChangesVariant = 'default') => {
      if (hasUnsavedData) {
        setPendingCallback(() => () => {
          window.location.href = url;
        });
        setDialogVariant(variant);
        setShowDialog(true);
      } else {
        // Mark as intentional navigation to suppress beforeunload
        isIntentionalNavigation.current = true;
        window.location.href = url;
      }
    },
    [hasUnsavedData]
  );

  const confirmAction = useCallback(
    (action: () => void) => {
      if (hasUnsavedData) {
        setPendingCallback(() => action);
        setShowDialog(true);
      } else {
        // Mark as intentional action to suppress beforeunload (in case action causes page unload)
        isIntentionalNavigation.current = true;
        action();
      }
    },
    [hasUnsavedData]
  );

  // Native beforeunload listener for browser-level actions (refresh, tab close, etc.)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Skip if this is an intentional navigation (user already confirmed via custom dialog)
      if (isIntentionalNavigation.current) {
        return;
      }

      if (hasUnsavedData) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedData]);

  // Use ref to get latest confirmNavigation in event listener
  const confirmNavigationRef = useRef(confirmNavigation);
  useEffect(() => {
    confirmNavigationRef.current = confirmNavigation;
  }, [confirmNavigation]);

  // Listen for autoLoginRequired event from ClientAuthProvider
  useEffect(() => {
    const handleAutoLoginRequired = (e: CustomEvent<{ url: string }>) => {
      confirmNavigationRef.current(e.detail.url, 'autoLogin');
    };

    window.addEventListener(
      'autoLoginRequired',
      handleAutoLoginRequired as EventListener
    );

    return () => {
      window.removeEventListener(
        'autoLoginRequired',
        handleAutoLoginRequired as EventListener
      );
    };
  }, []);

  // Listen for authStateResolved event to close any open dialog (cross-tab scenario)
  // This handles cases where user completed login/logout in another tab
  useEffect(() => {
    const handleAuthStateResolved = () => {
      // Close the dialog if it's open (regardless of variant)
      // This covers: AUTO_LOGIN, AUTO_LOGOUT, manual login, manual logout
      if (showDialog) {
        setShowDialog(false);
        setPendingCallback(null);
        setDialogVariant('default');
      }
    };

    window.addEventListener('authStateResolved', handleAuthStateResolved);

    return () => {
      window.removeEventListener('authStateResolved', handleAuthStateResolved);
    };
  }, [showDialog]);

  const handleConfirm = useCallback(() => {
    explorerAnalytics.unsavedChangesAction({
      action: 'proceed',
      variant: dialogVariant,
    });
    setShowDialog(false);
    // Mark as intentional navigation to suppress beforeunload
    isIntentionalNavigation.current = true;
    pendingCallback?.();
    setPendingCallback(null);
  }, [pendingCallback, dialogVariant]);

  const handleCancel = useCallback(() => {
    explorerAnalytics.unsavedChangesAction({
      action: 'cancel',
      variant: dialogVariant,
    });
    setShowDialog(false);
    setPendingCallback(null);
    setDialogVariant('default');
  }, [dialogVariant]);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setShowDialog(false);
      setPendingCallback(null);
      setDialogVariant('default');
    }
  }, []);

  const dialogState = React.useMemo(
    () => ({
      open: showDialog,
      onConfirm: handleConfirm,
      onCancel: handleCancel,
      onOpenChange: handleOpenChange,
      variant: dialogVariant,
    }),
    [showDialog, handleConfirm, handleCancel, handleOpenChange, dialogVariant]
  );

  const value = React.useMemo(
    () => ({
      hasUnsavedData,
      confirmNavigation,
      confirmAction,
      dialogState,
    }),
    [hasUnsavedData, confirmNavigation, confirmAction, dialogState]
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
    </UnsavedChangesContext.Provider>
  );
};
